import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiArrowRight, FiX, FiUser, FiMapPin, FiMessageSquare, FiCheck } = FiIcons;

const SERVICES = [
  'Electrical Installation',
  'CCTV Systems',
  'Smart Home Automation',
  'Solar Power',
  'Security Systems',
  'Annual Maintenance (AMC)',
  'Emergency Repair',
  'Wiring & Rewiring',
  'Other',
];

const QuoteModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: '', address: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.phone || !form.service) return setError('Please fill Name, Phone and Service.');
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          service: form.service,
          message: `Address: ${form.address}\n\n${form.message}`.trim(),
          source: 'Free Quote Popup',
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to submit quote request.');
      setSuccess(true);
    } catch (error) { setError(error.message || 'Could not submit. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-lg bg-gray-900 border border-gray-700 rounded-3xl overflow-hidden shadow-2xl"
      >
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-black text-xl">Get Your Free Quote</h2>
              <p className="text-blue-200 text-xs mt-0.5">We'll respond within 2 hours</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors">
              <SafeIcon icon={FiX} className="text-sm" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {success ? (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 border-2 border-green-500/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheck} className="text-3xl text-green-400" />
              </div>
              <h3 className="text-white font-black text-xl mb-2">Quote Request Sent! 🎉</h3>
              <p className="text-gray-400 text-sm mb-1">We'll contact you within 2 hours.</p>
              <p className="text-blue-400 text-sm">{form.phone}</p>
              <button onClick={onClose} className="mt-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold px-8 py-3 rounded-2xl">
                Close
              </button>
            </motion.div>
          ) : (
            <div className="space-y-3">
              {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-500/30 rounded-xl p-3">{error}</p>}
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
                <input name="name" type="text" placeholder="Full Name *" value={form.name} onChange={handle}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors" />
              </div>
              <div className="relative">
                <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
                <input name="phone" type="tel" placeholder="Phone Number *" value={form.phone} onChange={handle}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors" />
              </div>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
                <input name="email" type="email" placeholder="Email (optional)" value={form.email} onChange={handle}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors" />
              </div>
              <select name="service" value={form.service} onChange={handle}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-sm focus:border-blue-500 focus:outline-none transition-colors"
                style={{ color: form.service ? 'white' : '#6b7280' }}>
                <option value="" disabled>Select Service Needed *</option>
                {SERVICES.map(s => <option key={s} value={s} className="bg-gray-800 text-white">{s}</option>)}
              </select>
              <div className="relative">
                <SafeIcon icon={FiMapPin} className="absolute left-4 top-3.5 text-gray-500 text-sm" />
                <input name="address" type="text" placeholder="Full Address" value={form.address} onChange={handle}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors" />
              </div>
              <div className="relative">
                <SafeIcon icon={FiMessageSquare} className="absolute left-4 top-3.5 text-gray-500 text-sm" />
                <textarea name="message" placeholder="Additional Details (optional)" value={form.message} onChange={handle} rows={3}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors resize-none" />
              </div>
              <button onClick={submit} disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-3 rounded-2xl flex items-center justify-center space-x-2 disabled:opacity-50 hover:from-blue-700 hover:to-cyan-700 transition-all">
                <span>{loading ? 'Submitting...' : 'Submit Quote Request'}</span>
                {!loading && <SafeIcon icon={FiArrowRight} />}
              </button>
              <p className="text-gray-600 text-xs text-center">* Required fields • We respond within 2 hours</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const CTA = () => {
  const [showModal, setShowModal] = useState(false);

  // ✅ Listen for event from Testimonials button
  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openQuoteModal', handler);
    return () => window.removeEventListener('openQuoteModal', handler);
  }, []);

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-blue-900/20 via-black to-cyan-900/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-xl animate-pulse" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-purple-500/10 rounded-full blur-2xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="space-y-8">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" /><span>Ready to Get Started?</span>
            </motion.div>

            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold">
              <span className="gradient-text">Let's Power Your</span><br /><span className="text-white">Next Project Together</span>
            </motion.h2>

            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} viewport={{ once: true }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              From simple electrical repairs to complete smart home transformations, we're here to bring your vision to life with professional expertise and reliable service.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} viewport={{ once: true }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button
                onClick={() => setShowModal(true)}
                className="group flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full text-white font-semibold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 glow-blue-strong">
                <SafeIcon icon={FiMail} className="text-lg" />
                <span>Get Your Free Quote</span>
                <SafeIcon icon={FiArrowRight} className="text-lg group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <a href="tel:+919906669918"
                className="group flex items-center space-x-3 border-2 border-white/20 px-8 py-4 rounded-full text-white font-semibold text-lg hover:border-blue-400 hover:bg-blue-400/10 transition-all duration-300">
                <SafeIcon icon={FiPhone} className="text-lg" /><span>Call: +91 99066 69918</span>
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} viewport={{ once: true }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 text-sm text-gray-400">
              <div className="flex items-center space-x-2"><span className="w-2 h-2 bg-green-400 rounded-full" /><span>Free Consultation</span></div>
              <div className="flex items-center space-x-2"><span className="w-2 h-2 bg-blue-400 rounded-full" /><span>24/7 Emergency Service</span></div>
              <div className="flex items-center space-x-2"><span className="w-2 h-2 bg-purple-400 rounded-full" /><span>Licensed & Insured</span></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {showModal && <QuoteModal onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </>
  );
};

export default CTA;
